import {Manifest} from '../../api/model/manifest';
import {roundPercentPreservingExtremes} from './report-leaves';

export interface ReportDisplayConfigColumn {
  key: string;
  label?: string;
  visible: boolean;
  /** Siehe ReportColumnConfig.perStep (my-checks-builder.model.ts) – gleiche Semantik. */
  perStep?: boolean;
}

export interface ReportDisplayConfigLevel {
  key: string;
  label?: string;
  columns: ReportDisplayConfigColumn[];
}

export interface ReportDisplayConfig {
  rulesetId?: string;
  rulesetType?: string;
  perspective?: string;
  levels?: ReportDisplayConfigLevel[];
}

export interface ReportSelectionContext {
  selectedRuleSetIds: string[];
  selectedRuleIds: string[];
  selectedRulesetType?: string;
}

export interface ReportDisplayResolveContext {
  selection?: Partial<ReportSelectionContext>;
  checkType?: string;
}

export function parseManifest(input?: string | Manifest): Manifest | null {
  if (!input) return null;
  if (typeof input !== 'string') return input;
  try {
    return JSON.parse(input) as Manifest;
  } catch {
    return null;
  }
}

export function extractReportSelectionFromManifest(input?: string | Manifest): ReportSelectionContext {
  const manifest = parseManifest(input);
  if (!manifest) {
    return {selectedRuleSetIds: [], selectedRuleIds: []};
  }

  const ruleStep = (manifest.steps ?? []).find(step =>
    step.moduleId === 'rule_selection' || step.moduleId === 'check_selector',
  );
  if (!ruleStep) {
    return {selectedRuleSetIds: [], selectedRuleIds: []};
  }

  const settings = ruleStep.settings ?? {};
  const config = ruleStep.interactionRequired?.config ?? {};
  const ruleTypes = asStringArray(settings['ruleTypes'] ?? config['ruleTypes']);

  return {
    selectedRuleSetIds: asStringArray(settings['selectedRuleSetIds'] ?? config['selectedRuleSetIds']),
    selectedRuleIds: asStringArray(settings['selectedRuleIds'] ?? config['selectedRuleIds']),
    selectedRulesetType: normalizeRulesetType(settings['selectedRulesetType'])
      ?? (ruleTypes.includes('HYBRID') ? 'HYBRID' : ruleTypes.includes('KI') ? 'KI' : undefined),
  };
}

export function resolveReportDisplayConfig(
  input?: string | Manifest,
  context?: ReportDisplayResolveContext,
): ReportDisplayConfig | null {
  const manifest = parseManifest(input);
  if (!manifest) return null;

  const reportConfig = manifest.trigger?.config?.['reportConfig'];
  const configs = Array.isArray(reportConfig?.['reportConfigs'])
    ? reportConfig['reportConfigs'] as ReportDisplayConfig[]
    : [];
  if (configs.length === 0) return null;

  const manifestSelection = extractReportSelectionFromManifest(manifest);
  const selection: ReportSelectionContext = {
    selectedRuleSetIds: asStringArray(context?.selection?.selectedRuleSetIds ?? manifestSelection.selectedRuleSetIds),
    selectedRuleIds: asStringArray(context?.selection?.selectedRuleIds ?? manifestSelection.selectedRuleIds),
    selectedRulesetType: normalizeRulesetType(context?.selection?.selectedRulesetType ?? manifestSelection.selectedRulesetType),
  };

  return selectReportDisplayConfig(configs, selection, context?.checkType);
}

/**
 * Wählt aus einer Liste von Berichtskonfigurationen die passende Einzel-Config aus
 * (per ausgewählter Regelset-ID, sonst per Prüfregeltyp, sonst die erste). Gemeinsamer Kern
 * für Manifest- und Lauf-Snapshot-Auflösung.
 */
function selectReportDisplayConfig(
  configs: ReportDisplayConfig[],
  selection: ReportSelectionContext,
  checkType?: string,
): ReportDisplayConfig | null {
  if (!Array.isArray(configs) || configs.length === 0) return null;

  const selectedIds = new Set([...selection.selectedRuleSetIds, ...selection.selectedRuleIds]);
  if (selectedIds.size > 0) {
    const byRulesetId = configs.find(config => !!config.rulesetId && selectedIds.has(config.rulesetId));
    if (byRulesetId) return byRulesetId;
  }

  const preferredType = normalizeRulesetType(checkType ?? selection.selectedRulesetType);
  if (preferredType) {
    const byType = configs.find(config => normalizeRulesetType(config.rulesetType) === preferredType);
    if (byType) return byType;
  }

  return configs[0] ?? null;
}

/**
 * Liefert die Anzeige-Config **eines bestimmten Regelsets** aus einem Manifest.
 *
 * Gegenstück zu {@link resolveReportDisplayConfig}, das eine Liste von N Konfigurationen auf genau
 * eine reduziert. Bei einem gemischten Lauf (ein Teilbericht je Regelset) braucht jede
 * Stepper-Seite ihre eigene Config – die Zuordnung läuft über `rulesetId`.
 *
 * Fällt bewusst NICHT auf `configs[0]` zurück: eine fremde Config wäre schlechter als keine
 * (die Standard-Ebenen des Berichts greifen dann). Der Typ-Match bleibt als Notausgang für
 * Konfigurationen aus einer Zeit, in der noch keine `rulesetId` mitgeschrieben wurde.
 */
export function resolveReportDisplayConfigForRuleset(
  input: string | Manifest | undefined,
  rulesetId: string | undefined,
  checkType?: string,
): ReportDisplayConfig | null {
  const manifest = parseManifest(input);
  const configs = manifest?.trigger?.config?.['reportConfig']?.['reportConfigs'];
  if (!Array.isArray(configs) || configs.length === 0) return null;

  const typed = configs as ReportDisplayConfig[];
  if (rulesetId) {
    const byRulesetId = typed.find(config => config.rulesetId === rulesetId);
    if (byRulesetId) return byRulesetId;
  }

  const preferredType = normalizeRulesetType(checkType);
  if (preferredType) {
    const byType = typed.find(config => normalizeRulesetType(config.rulesetType) === preferredType);
    if (byType) return byType;
  }

  return null;
}

function isSnapshotDisplayConfig(value: unknown): value is ReportDisplayConfig {
  const levels = (value as ReportDisplayConfig | undefined)?.levels;
  return !!value && typeof value === 'object' && Array.isArray(levels) && levels.length > 0;
}

/**
 * Baut die Anzeige-Config aus dem eingefrorenen Lauf-Snapshot (`instance.outputData`) statt aus der
 * (evtl. inzwischen geänderten) myCheck-Definition. Dadurch bleibt ein bereits gelaufener Bericht
 * stabil, auch wenn die Berichtseinstellungen des myChecks später angepasst werden.
 *
 * Bevorzugt die bereits aufgelöste `effectiveReportConfig`; fällt sonst auf `reportConfig.reportConfigs[]`
 * zurück (Auswahl per rulesetId/Typ, gleiche Logik wie beim Manifest-Pfad). Gibt `null` zurück, wenn der
 * Lauf keinen (verwertbaren) Snapshot enthält (Alt-Läufe) – dann greift der Aufrufer auf die Definition
 * zurück. Der Snapshot ist bereits final normalisiert und wird unverändert übernommen.
 */
export function resolveReportDisplayConfigFromSnapshot(
  outputData: unknown,
  context?: ReportDisplayResolveContext,
): ReportDisplayConfig | null {
  if (!outputData || typeof outputData !== 'object') return null;
  const data = outputData as Record<string, any>;

  // 1. Bereits aufgelöste Einzel-Config des Laufs bevorzugen (enthält ggf. structureIdentifikatorParamId).
  const effective = data['effectiveReportConfig'];
  if (isSnapshotDisplayConfig(effective)) {
    return effective as ReportDisplayConfig;
  }

  // 2. Sonst aus der eingefrorenen Liste auswählen (falls vorhanden).
  const reportConfig = data['reportConfig'];
  const configs = Array.isArray(reportConfig?.['reportConfigs'])
    ? reportConfig['reportConfigs'] as ReportDisplayConfig[]
    : [];
  if (configs.length === 0) return null;

  const selection: ReportSelectionContext = {
    selectedRuleSetIds: asStringArray(context?.selection?.selectedRuleSetIds ?? data['selected_ruleset_ids']),
    selectedRuleIds: asStringArray(context?.selection?.selectedRuleIds ?? data['selected_rule_ids']),
    selectedRulesetType: normalizeRulesetType(context?.selection?.selectedRulesetType ?? data['selected_ruleset_type']),
  };

  return selectReportDisplayConfig(configs, selection, context?.checkType);
}

function normalizeRulesetType(value: unknown): 'IDS' | 'HYBRID' | 'KI' | undefined {
  if (typeof value !== 'string') return undefined;
  const upper = value.toUpperCase();
  if (upper === 'AI' || upper === 'HOAI' || upper === 'KI') return 'KI';
  if (upper === 'HYBRID') return 'HYBRID';
  if (upper === 'IDS') return 'IDS';
  return undefined;
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string')
    : [];
}

// ─── Dynamische Auflösung von Detail-Spalten (benutzerdefiniertes KI-Ausgabeformat) ──

/** Spalten-Schlüssel, die rein als Status interpretiert werden (über das Icon dargestellt). */
const STATUS_COLUMN_RE = /^status$/i;
/**
 * Spalten-Schlüssel, deren Wert das fachliche Prüfergebnis repräsentiert.
 *
 * ACHTUNG – Spiegel im Backend: muss identisch zu `VERDICT_KEY_RE` in
 * `BIC-Workflows/shared/ai_verdict.py` bleiben. Weicht die Liste ab, kippt ein Feldname
 * den Modul-/Regel-Status, ohne das Status-Icon im Bericht mitzunehmen (oder umgekehrt).
 */
const VERDICT_COLUMN_RE = /^(ergebnis|result|erf[üu]llt|erfuellt|bestanden|passed|erfolgreich)$/i;

/**
 * Die Stufen des fachlichen Prüfergebnisses.
 *
 * `unknown` ist ein vollwertiges Ergebnis („nicht entscheidbar" / „für diese Regel nicht
 * relevant"), kein Fehlerfall – Spiegel von `UNKNOWN` in `shared/ai_verdict.py`.
 */
export type VerdictStatus = 'success' | 'warning' | 'error' | 'unknown';

// Reihenfolge ist bedeutsam: die Fehler-Liste wird zuerst geprüft, damit
// "nicht erfüllt" nicht am "erfüllt" hängen bleibt.
const VERDICT_ERROR_VALUES = [
  'failure', 'failed', 'fail', 'error', 'false', 'falsch', 'nein', 'no',
  'nicht erfüllt', 'nicht erfuellt', 'nicht bestanden', 'fehlgeschlagen', 'durchgefallen', 'fehler',
];
const VERDICT_WARNING_VALUES = [
  'warning', 'warnung', 'warn', 'teilweise', 'teilweise erfüllt', 'teilweise erfuellt',
  'partial', 'hinweis', 'eingeschränkt', 'eingeschraenkt',
];
const VERDICT_SUCCESS_VALUES = [
  'true', 'wahr', 'ja', 'yes', 'erfüllt', 'erfuellt', 'bestanden',
  'richtig', 'korrekt', 'erfolgreich', 'passed', 'pass', 'ok', 'success',
];
/**
 * „Nicht entscheidbar" – wird VOR den übrigen Listen geprüft, damit „nicht bewertbar"/„nicht
 * anwendbar" nicht am Fehler-Vokabular hängen bleibt. Spiegel von `_UNKNOWN_RE` in
 * `shared/ai_verdict.py`.
 */
const VERDICT_UNKNOWN_VALUES = [
  'unknown', 'unbekannt', 'nicht relevant', 'nicht bewertbar', 'nicht anwendbar',
  'keine ausreichende information', 'n/a', 'na',
];

/**
 * Interpretiert einen Ausgabefeld-Wert als fachliches Prüfergebnis; `null`, wenn der Wert
 * kein erkennbares Ergebnis ist. Es findet ausdrücklich KEIN stiller Fallback auf
 * `'success'` statt – ein unklares Ergebnis darf nie als bestanden (grün) durchgehen.
 *
 * `null` (kein erkennbares Ergebnis) und `'unknown'` (ausdrücklich „nicht entscheidbar") sind
 * zwei verschiedene Dinge: das erste ist ein Datenproblem, das zweite eine Aussage der Prüfung.
 * Beide dürfen nie zu „bestanden" werden.
 *
 * Spiegel von `normalize_verdict` in `BIC-Workflows/shared/ai_verdict.py`.
 */
export function parseVerdictValue(value: unknown): VerdictStatus | null {
  if (typeof value === 'boolean') return value ? 'success' : 'error';
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value >= 0.5 ? 'success' : 'error';
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (!normalized) return null;
    if (VERDICT_UNKNOWN_VALUES.includes(normalized)) return 'unknown';
    if (VERDICT_ERROR_VALUES.includes(normalized)) return 'error';
    if (VERDICT_WARNING_VALUES.includes(normalized)) return 'warning';
    if (VERDICT_SUCCESS_VALUES.includes(normalized)) return 'success';
  }
  return null;
}

/**
 * Findet in den dynamischen KI-Ausgabefeldern das Ergebnis-Feld (z. B. "Ergebnis": FAILURE).
 * Dieses steuert das Status-Icon des Details und wird deshalb nicht zusätzlich als
 * Textzeile dargestellt.
 */
export function findVerdictInFields(fields?: Record<string, any> | null): VerdictStatus | null {
  if (!fields) return null;
  for (const [key, raw] of Object.entries(fields)) {
    if (!VERDICT_COLUMN_RE.test(key.trim())) continue;
    const verdict = parseVerdictValue(raw);
    if (verdict !== null) return verdict;
  }
  return null;
}
/** Spalten-Schlüssel, die als numerische Bewertung interpretiert werden. */
const RATING_COLUMN_RE = /^(rating|bewertung|score|note|wert)$/i;
/** Spalten-Schlüssel, die als Antwort-/Begründungstext interpretiert werden. */
const ANSWER_COLUMN_RE = /^(result|ergebnis|antwort|answer|begr[üu]ndung|beschreibung|text)$/i;

/** Schlüssel des KI-Freitext-Antwortfeldes (ohne Verdikt-/Ergebnis-Felder). */
const ANSWER_TEXT_KEY_RE = /^(antwort|answer|begr[üu]ndung|erl[äa]uterung|beschreibung|text)$/i;

/**
 * Feldschlüssel, die per Definition je Prüfschritt variieren und nie als regelweiter
 * Regelparameter gelten dürfen — unabhängig von Spaltenkonfiguration oder Datenvergleich
 * (ein Konfidenzwert gehört zur einzelnen KI-Antwort, nicht zur Regel).
 */
const ALWAYS_PER_STEP_COLUMN_RE = /^(konfidenz|confidence)$/i;

/**
 * Ob ein Feldschlüssel das KI-Freitext-Antwortfeld ist. Dieses Feld erhält im Bericht als
 * Überschrift den Prüfschritt-Titel (statt des generischen Parameternamens „Antwort").
 */
export function isAnswerTextKey(key: string): boolean {
  return ANSWER_TEXT_KEY_RE.test((key ?? '').trim());
}

/** Minimale Detail-Form, aus der ein Spaltenwert dynamisch aufgelöst werden kann. */
export interface ResolvableDetail {
  fields?: Record<string, any> | null;
  text?: string;
  count?: number;
}

/** Case-insensitiver Schlüssel-Lookup in den dynamischen Feldern. */
function resolveFieldKey(fields: Record<string, any>, columnKey: string): string | undefined {
  return columnKey in fields
    ? columnKey
    : Object.keys(fields).find(k => k.toLowerCase() === columnKey.toLowerCase());
}

/** Eine darstellbare Detail-Spalte (Label + aufgelöster Wert). */
export interface ResolvedDetailEntry {
  /** Roher Feld-/Spaltenschlüssel (für den Abgleich mit den tatsächlichen Ausgabefeldern). */
  key: string;
  label: string;
  value: string;
  /**
   * Ob der Wert laut Berichtskonfiguration pro Prüfschritt variieren kann (KI-Generiert) oder
   * für alle Prüfschritte derselben Regel konstant ist (Prüfparameter/Eigener Wert). Nur `false`,
   * wenn eine Spalte explizit als konstant konfiguriert wurde; nicht abgedeckte Felder gelten hier
   * als variabel. Bei fehlender expliziter Konfiguration (`perStepExplicit` falsch) trifft die
   * Anzeige zusätzlich eine datengetriebene Zuordnung (identischer Wert über alle Prüfschritte ⇒
   * konstant), damit auch alte/nicht migrierte Berichtskonfigurationen korrekt aufgeteilt werden.
   */
  perStep: boolean;
  /**
   * Ob `perStep` aus einer expliziten Spaltenkonfiguration stammt (`column.perStep === true`),
   * statt nur aus dem `undefined`-Default. Explizit konfigurierte Prüfschritt-Felder dürfen vom
   * datengetriebenen Konstant-Abgleich in `report-detail-blocks.ts` nicht überstimmt werden –
   * sonst landet ein als "Prüfschritt" konfiguriertes Feld fälschlich bei den Regelparametern,
   * sobald die KI zufällig für mehrere Schritte denselben Text liefert.
   */
  perStepExplicit: boolean;
}

/** Formatiert einen rohen KI-Feldwert für die Anzeige. */
export function formatReportFieldValue(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : null;
  }
  if (typeof value === 'boolean') return value ? 'Ja' : 'Nein';
  if (Array.isArray(value)) {
    const parts = value.map(v => formatReportFieldValue(v)).filter((v): v is string => v !== null);
    return parts.length > 0 ? parts.join(', ') : null;
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return null;
    }
  }
  const text = String(value).trim();
  return text.length > 0 ? text : null;
}

function formatRatingPercent(value: number): string {
  const pct = value <= 1 ? value * 100 : value;
  // Dieselbe Invariante wie im restlichen Bericht: ein Rohwert unter 1.0 darf nicht als
  // „100 %" erscheinen (siehe roundPercentPreservingExtremes).
  return `${roundPercentPreservingExtremes(pct)} %`;
}

/**
 * Löst den Anzeigewert einer konfigurierten Detail-Spalte gegen die dynamischen
 * KI-Ausgabefelder auf. Die Spalten-Schlüssel entsprechen den benutzerdefinierten
 * Ausgabeparameter-Namen; es werden keine festen Felder erzwungen.
 *
 * Reihenfolge:
 *  1. Direkter/­case-insensitiver Treffer im `fields`-Objekt (dynamisch, benutzerdefiniert).
 *  2. Bewertungs-Spalte: normalisierter Prozentwert aus `rating`-Kompatibilitätsfeld.
 *  3. Status-Spalte: kein Textwert (über das Status-Icon dargestellt).
 */
export function resolveDetailColumnValue(detail: ResolvableDetail, columnKey: string): string | null {
  const fields = detail.fields ?? {};
  const directKey = resolveFieldKey(fields, columnKey);
  const rawValue = directKey !== undefined ? fields[directKey] : undefined;

  const ratingLike = RATING_COLUMN_RE.test(columnKey) || (directKey !== undefined && RATING_COLUMN_RE.test(directKey));
  if (ratingLike && typeof rawValue === 'number' && Number.isFinite(rawValue)) {
    return formatRatingPercent(rawValue);
  }

  if (directKey !== undefined) {
    const formatted = formatReportFieldValue(rawValue);
    if (formatted !== null) return formatted;
  }

  // Kompatibilität mit veralteten/festen Spalten-Schlüsseln: Antworttext aus dem
  // generischen Kompatibilitätsfeld auflösen, wenn kein passendes dynamisches Feld existiert.
  if (ANSWER_COLUMN_RE.test(columnKey) && detail.text && detail.text.trim()) {
    return detail.text.trim();
  }

  if (STATUS_COLUMN_RE.test(columnKey)) {
    return null;
  }

  return null;
}

/**
 * Liefert die darzustellenden Detail-Einträge in konfigurierter Reihenfolge.
 * Wenn keine konfigurierte Spalte einen Wert liefert (z. B. veraltete Manifeste,
 * deren Spalten-Schlüssel nicht zu den dynamischen Feldnamen passen), werden als
 * Fallback alle vorhandenen KI-Felder generisch angezeigt, damit nichts verloren geht.
 */
export function resolveDetailEntries(
  detail: ResolvableDetail,
  columns: { key: string; label?: string; perStep?: boolean }[],
): ResolvedDetailEntry[] {
  const entries: ResolvedDetailEntry[] = [];
  const coveredKeys = new Set<string>();
  for (const column of columns) {
    coveredKeys.add(column.key.toLowerCase());
    if (STATUS_COLUMN_RE.test(column.key)) continue;
    // Ergebnis-/Verdict-Felder werden ausschließlich über das Status-Icon dargestellt,
    // nie als Text – unabhängig davon, ob der Wert boolesch oder ausgeschrieben ist.
    if (VERDICT_COLUMN_RE.test(column.key)) continue;
    const value = resolveDetailColumnValue(detail, column.key);
    if (value !== null) {
      // "Konfidenz"/"Confidence" gehört zur einzelnen KI-Antwort und darf nie – auch nicht durch
      // eine veraltete/fehlende Spaltenkonfiguration – als regelweiter Regelparameter landen.
      const forcedPerStep = ALWAYS_PER_STEP_COLUMN_RE.test(column.key);
      entries.push({
        key: column.key,
        label: column.label || column.key,
        value,
        perStep: forcedPerStep ? true : column.perStep !== false,
        perStepExplicit: forcedPerStep ? true : column.perStep === true,
      });
    }
  }

  // Alle übrigen KI-Ausgabefelder anhängen, die von keiner konfigurierten Spalte abgedeckt sind.
  // So verwirft eine veraltete/unvollständige Berichtskonfiguration niemals Felder, die die KI
  // tatsächlich geliefert hat (z. B. ein nachträglich ergänzter Ausgabeparameter).
  const fields = detail.fields ?? {};
  for (const [key, raw] of Object.entries(fields)) {
    if (coveredKeys.has(key.toLowerCase())) continue;
    if (VERDICT_COLUMN_RE.test(key)) continue;
    const value = formatReportFieldValue(raw);
    if (value !== null) {
      const forcedPerStep = ALWAYS_PER_STEP_COLUMN_RE.test(key);
      entries.push({key, label: key, value, perStep: true, perStepExplicit: forcedPerStep});
    }
  }

  if (entries.length > 0) return entries;

  if (detail.text && detail.text.trim()) {
    entries.push({
      key: 'Ergebnis',
      label: 'Ergebnis',
      value: detail.text.trim(),
      perStep: true,
      perStepExplicit: false
    });
  }
  return entries;
}
